"use client";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import log from "logbeacon";
import { useMemoizedFn } from "ahooks";
import { useQueryClient } from "@tanstack/react-query";
import { ClickNotificationInfo, OnChartClickProps } from "../types";
import { EdggyTimeLine } from "./EdggyTimeLine";
import { EdggyTimeLineRef } from "./EdggyTimeLine/EdggyTimeLine";
import Translations from "@translate/components/CoPilot/CopilotAgent/EdggyAgent/EdggyAgent";
import { I18nUtil } from "@utils/i18n";
import { DropDown } from "@/src/components/DropDown";
import NextImage from "@/src/components/NextImage";
import { InsertConfig, useAgentReportList } from "@/src/hooks";
import {
  AgentReport,
  AgentReportLevelEnum,
} from "@/src/features/api/memberApi";
import { useOnboardingData } from "@/src/components/Portfolio/Onboarding/useOnboardingData";
import {
  NOTI_AGENT_REPORT_HANDLER_ID,
  useWebSocketNotificationContext,
} from "@/src/features";
import { QUERY_KEYS } from "@/src/config";

const I18n = I18nUtil.createScoped(Translations);

interface MasterAgentProps {
  agentId: string;
  agentName: string;
  agentLabel: string;
  onChartClick: (value: OnChartClickProps) => void;
  viewDailyReport?: boolean;
  notificationInfo?: ClickNotificationInfo | null;
}

/**
 * 默认插入配置
 */
const wsInsertConfig: InsertConfig = {
  position: "after",
  matcher: (
    item: AgentReport,
    index: number,
    _groupKey: string,
    groupIndex: number,
  ) => {
    return groupIndex === 0 && item.type === "DAILY_REPORT" && index === 0;
  },
  searchGroups: 1,
};

export const MasterAgent = ({
  agentId: _agentId,
  agentName: _agentName,
  agentLabel: _agentLabel,
  onChartClick,
  viewDailyReport = false,
  notificationInfo,
}: MasterAgentProps) => {
  const { listItems: _listItems } = useOnboardingData(false, true);
  const handledClickNotificationFlagRef = useRef(false);
  const queryClient = useQueryClient();
  const dropDownOptions = useMemo(() => {
    const excludedLabels = ["edggy"];
    const options = _listItems
      .filter(
        (item) =>
          item.type === "agent" && !excludedLabels.includes(item.label ?? ""),
      )
      .map((item) => {
        return {
          value: item.id,
          label: item.name ?? "",
        };
      });
    return options;
  }, [_listItems]);
  const [dropDownValue, setDropDownValue] = useState<string[]>([]);

  const edggyTimeLineRef = useRef<EdggyTimeLineRef>(null);
  // 处理 agent_ids：拆分逗号拼接的 ID，过滤掉 "all"，如果全选则返回空数组
  const agentIds = useMemo(() => {
    // 如果 dropDownValue 的长度和 dropDownOptions 长度一致，表示全选，返回空数组
    if (dropDownValue.length === dropDownOptions.length) {
      return [];
    }
    // 拆分每一项中可能存在的逗号拼接的 ID
    const allIds = dropDownValue
      .filter((id) => id !== "all") // 过滤掉 "all"
      .flatMap((id) => {
        // 将逗号分隔的字符串拆分成数组
        return id
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      });
    // 去重
    const uniqueIds = Array.from(new Set(allIds));
    return uniqueIds.length > 0 ? uniqueIds : [];
  }, [dropDownValue, dropDownOptions]);

  const {
    data: agentReportList,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage: fetchNextPage,
  } = useAgentReportList({
    params: {
      levels: [AgentReportLevelEnum.HIGH],
      agent_ids: agentIds,
      "enrich.enable": true,
      "enrich.with_asset.enable": true,
      "enrich.with_asset.with_price": true,
    },
  });

  // 过滤掉通知消息，避免重复渲染
  const filteredAgentReportList = useMemo(() => {
    if (notificationInfo && !handledClickNotificationFlagRef.current) {
      // 如果有通知且还未处理，过滤掉该消息
      return agentReportList.filter(
        (item) =>
          !(
            item.id === notificationInfo.msgId &&
            item.agent_id === notificationInfo.msgAgentId
          ),
      );
    }
    return agentReportList;
  }, [agentReportList, notificationInfo]);

  const { updateHandler: updateHandlerWebSocketNotification } =
    useWebSocketNotificationContext();

  useEffect(() => {
    log.debug("[EdggyAgent]dropDownValue", dropDownValue);
  }, [dropDownValue]);

  // 当检测到 notificationInfo 时，使缓存失效以获取最新数据
  useEffect(() => {
    if (notificationInfo && !handledClickNotificationFlagRef.current) {
      log.debug(
        "[EdggyAgent][notification] Invalidating cache for notification:",
        notificationInfo,
      );

      // 使 AGENT_REPORT_LIST 相关的所有查询失效
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.AGENT_REPORT_LIST],
        refetchType: "active", // 只重新请求当前活跃的查询
      });
    }
  }, [notificationInfo, queryClient]);

  // 记录过滤逻辑的执行情况
  useEffect(() => {
    if (notificationInfo && !handledClickNotificationFlagRef.current) {
      log.debug(
        "[EdggyAgent][notification] Filtering notification message from data",
        {
          msgId: notificationInfo.msgId,
          msgAgentId: notificationInfo.msgAgentId,
          totalItems: agentReportList.length,
          filteredItems: filteredAgentReportList.length,
          filtered: agentReportList.length !== filteredAgentReportList.length,
        },
      );
    }
  }, [notificationInfo, agentReportList, filteredAgentReportList]);

  // 创建稳定的 WebSocket handler
  const handleWebSocketMessage = useMemoizedFn((messages: AgentReport[]) => {
    log.debug(
      "[EdggyAgent][ws] Handler called with messages:",
      messages.length,
    );
    handleInsertData(messages);
  });

  // 注册 WebSocket handler
  useEffect(() => {
    log.debug("[EdggyAgent][ws] Registering WebSocket handler");

    updateHandlerWebSocketNotification(
      NOTI_AGENT_REPORT_HANDLER_ID,
      handleWebSocketMessage,
    );

    return () => {
      log.debug("[EdggyAgent][ws] Cleaning up WebSocket handler");
      updateHandlerWebSocketNotification(
        NOTI_AGENT_REPORT_HANDLER_ID,
        undefined,
      );
    };
  }, [updateHandlerWebSocketNotification, handleWebSocketMessage]);

  // 使用 useCallback 稳定 insertData 函数引用
  const handleInsertData = useMemoizedFn((data: AgentReport[]) => {
    if (data?.length > 0) {
      edggyTimeLineRef.current?.insertItems(data, wsInsertConfig);
    }
  });

  // 处理查看 daily report
  useEffect(() => {
    if (viewDailyReport && edggyTimeLineRef.current) {
      // 延迟执行，确保时间线已渲染
      setTimeout(() => {
        // 滚动到顶部（daily report 在时间线最顶部）
        edggyTimeLineRef.current?.scrollToTop(true);
        // 清除 URL 参数
        const url = new URL(window.location.href);
        url.searchParams.delete("viewDailyReport");
        window.history.replaceState({}, "", url.toString());
      }, 500);
    }
  }, [viewDailyReport]);

  useLayoutEffect(() => {
    // 只有在数据加载完成且不在加载中时才执行
    if (
      notificationInfo &&
      agentReportList?.length > 0 &&
      !isFetching &&
      !handledClickNotificationFlagRef.current
    ) {
      handledClickNotificationFlagRef.current = true;
      log.debug(
        "[EdggyAgent][notification click] Processing with fresh data, notificationInfo:",
        notificationInfo,
      );
      log.debug(
        "[EdggyAgent][notification click] agentReportList length:",
        agentReportList.length,
      );

      const data = agentReportList.find(
        (item: AgentReport) =>
          item.id === notificationInfo.msgId &&
          item.agent_id === notificationInfo.msgAgentId,
      );

      log.debug(
        "[EdggyAgent][notification click] find data ===",
        data ? JSON.stringify(data) : "NOT FOUND",
      );

      if (data) {
        // useLayoutEffect 会在 DOM 更新后同步执行
        requestAnimationFrame(() => {
          if (edggyTimeLineRef.current) {
            log.debug(
              "[EdggyAgent][notification click] Calling handleInsertData",
            );
            handleInsertData([{ ...data, created: Date.now() }]);
          }
        });
      } else {
        log.warn(
          "[EdggyAgent][notification click] Could not find notification data in agentReportList",
          {
            msgId: notificationInfo.msgId,
            msgAgentId: notificationInfo.msgAgentId,
            availableIds: agentReportList.map((item) => ({
              id: item.id,
              agent_id: item.agent_id,
            })),
          },
        );
      }
    }
  }, [notificationInfo, agentReportList, isFetching, handleInsertData]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none">
        <div className="flex justify-between items-center w-full pb-4">
          <div className="flex items-center gap-1">
            <NextImage
              src={"/assets/images/edgen-chat-icon.svg"}
              alt="agent-tip"
              width={20}
              height={20}
            />
            <p className="body_bold_l text-content_brand_primary">
              {I18n.t("Information for you")}
            </p>
          </div>
          <DropDown
            options={dropDownOptions}
            maxVisibleTags={1}
            enableSelectAll={true}
            value={dropDownValue}
            allOptionValue="all"
            onChange={(nextValue) => {
              setDropDownValue(nextValue);
            }}
            className="justify-end"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <EdggyTimeLine
          ref={edggyTimeLineRef}
          onChartClick={onChartClick}
          data={filteredAgentReportList as AgentReport[]}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetching={isFetching}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          agentId={_agentId}
        />
      </div>
    </div>
  );
};
